/**
 * How commands relate to the persisted session log.
 *
 * Imported by BOTH the browser (src/bridge.js) and the bridge server
 * (server/bridge-server.js), which is why this file has no dependencies — not
 * on three, not on the DOM, not on node. The two sides each keep a count of how
 * much of the log has been applied, and those counts have to agree exactly or
 * a reconnect either duplicates the scene or silently drops part of it. Sharing
 * the rules is cheaper than keeping two copies honest.
 */

/**
 * Commands that don't change the scene, and so aren't worth remembering.
 *
 * Note the direction of the default: anything NOT listed here counts as a
 * mutation. If you add a command and forget to classify it, the cost is a
 * redundant entry in the log. Listing mutations instead would mean a forgotten
 * command silently vanishes on reload — the exact bug the log exists to fix.
 */
export const READ_ONLY = new Set([
  'ping',
  'help',
  'listObjects',
  'inspectObject',
  'captureView',
  'resolveSelection',
  'meshDiagnostics',
  'getObjectSnapshot',
  'screenshot',
  'exportGLTF',
  'focusObject', // moves the camera, not the scene; the pose is tracked separately
  'getCamera',
  'setCamera',
  'orbitCamera',
  'getMeshData', // full topology readout for one mesh; nothing about the scene changes
  'getMeshAdjacency', // derived geometric regions/corners/edges for one mesh; nothing about the scene changes
  'history', // undo/redo status query; the scene and the log stay put
]);

/** Commands whose relay must be pinned to the bridge's authoritative revision. */
export const REVISION_GUARDED = new Set([
  'moveCorners',
  'restoreObjectSnapshot',
  // Shading changes how an object looks without changing its topology, so a
  // caller acting on a stale view of the scene would get no other warning: the
  // fingerprints it holds all still match. The revision is the only thing that
  // can tell it the object moved on.
  'setShading',
  // Welding rewrites connectivity and drops vertex ids. A caller holding a
  // resolved selection from before it would be addressing elements that no
  // longer exist, so it must prove it has seen the current scene.
  'weldVertices',
]);

const GUARDED_BATCH_REASON =
  'it requires authoritative baseRevision validation. Execute it individually.';

/** Commands whose semantics or binary results cannot be represented by batch. */
export const BATCH_EXCLUDED = new Map([
  ...[...REVISION_GUARDED].map((cmd) => [cmd, GUARDED_BATCH_REASON]),
  ['captureView', 'a batch cannot pin one revision across a set. Use the CLI captureSet command.'],
  ['screenshot', 'batch discards binary results. Call it individually.'],
  ['exportGLTF', 'batch discards binary results. Call it individually.'],
]);

/** Resolve exclusions dynamically so newly guarded commands cannot bypass batch preflight. */
export function batchExclusionReason(cmd) {
  if (REVISION_GUARDED.has(cmd)) return GUARDED_BATCH_REASON;
  return BATCH_EXCLUDED.get(cmd) ?? null;
}

/** Validate bridge-owned revision fields and return browser-safe relay arguments. */
export function prepareRelayArgs(cmd, args, authoritativeRevision) {
  const relayArgs = { ...(args ?? {}) };
  const baseRevision = relayArgs.baseRevision;
  delete relayArgs.baseRevision;

  if (REVISION_GUARDED.has(cmd)) {
    if (typeof baseRevision !== 'number') {
      return { ok: false, status: 400, error: `${cmd} requires a numeric baseRevision` };
    }
    if (baseRevision !== authoritativeRevision) {
      return {
        ok: false,
        status: 409,
        error: 'baseRevision does not match the authoritative revision',
        authoritativeRevision,
      };
    }
  }

  if (cmd === 'captureView') {
    const expectedRevision = relayArgs.expectedRevision;
    delete relayArgs.expectedRevision;
    if (expectedRevision !== undefined && typeof expectedRevision !== 'number') {
      return { ok: false, status: 400, error: 'captureView requires a numeric expectedRevision' };
    }
    if (expectedRevision !== undefined && expectedRevision !== authoritativeRevision) {
      return {
        ok: false,
        status: 409,
        error: 'expectedRevision does not match the authoritative revision',
        authoritativeRevision,
      };
    }
  }

  return { ok: true, args: relayArgs };
}

/**
 * Commands that make everything before them irrelevant.
 *
 * Replaying history that was already undone is pointless work and a chance for
 * drift, so these truncate the log instead of appending to it.
 */
export const RESETS_LOG = new Set(['clearScene']);

/** Commands that remove an object, taking its whole history with it. */
export const DELETES_OBJECT = new Set(['deleteObject']);

/**
 * Transforms that assign rather than accumulate.
 *
 * Setting one of these to an absolute value makes every earlier command of the
 * same kind on the same object irrelevant — including relative ones, since an
 * assignment overwrites whatever they had accumulated. With `relative: true`
 * they compose instead, and dropping any of them would change the result.
 */
export const ABSOLUTE_TRANSFORMS = new Set(['moveObject', 'rotateObject', 'scaleObject']);

/**
 * Commands that apply a partial patch to an object.
 *
 * Two of these on the same object collapse into one whose arguments are the
 * merge, later keys winning — exactly what applying them in order does.
 */
export const MERGEABLE = new Set(['setMaterial']);

/**
 * Absolute per-element edits that must merge by element id rather than by
 * whole-argument replacement.
 *
 * `setVertexPositions {id, changes}` carries `changes` as a list, not a flat
 * patch, so a naive MERGEABLE-style `{...oldArgs, ...newArgs}` merge would
 * silently drop every vertex edited by an earlier call but not the latest
 * one — the newer `changes` array would just replace the older one instead
 * of layering onto it. This keeps a mesh's replay-log footprint bounded to
 * one entry per (object, command) the same way MERGEABLE does, while merging
 * `changes` by vertex id, later value winning, so no earlier edit is lost.
 *
 * This is the log-compaction half of the mesh checkpoint story; the browser
 * additionally re-serializes the whole live scene after every mutating
 * command (see src/bridge.js `canonical`), which independently keeps a
 * mesh's log entry exactly current. Both mechanisms agree because both
 * converge on "one current entry per object" — this one is what keeps that
 * true even without a browser in the loop (e.g. registry-less history
 * tests), and is where future non-mergeable topology operations (extrude,
 * bevel, loop cut) will instead emit a fresh replacement entry rather than
 * attempting to merge structurally incompatible changes.
 */
export const VERTEX_MERGE = new Set(['setVertexPositions']);

/** True if this command should be remembered at all. */
export function isMutating(cmd) {
  return !READ_ONLY.has(cmd);
}
